import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import "@/index.css";

// ============================================================
// BULLETPROOF FIX: Patch Response.json() to prevent "clone" errors
// This ensures response body is read only once across ALL fetch calls
// ============================================================
(function patchResponseJson() {
  const originalJson = Response.prototype.json;
  Response.prototype.json = async function() {
    try {
      // If body was already consumed, this will fail
      // So we use text() + JSON.parse() as a fallback
      const text = await this.text();
      return JSON.parse(text);
    } catch (e) {
      // If text() also fails, try original json() as last resort
      // This handles edge cases where body is a different type
      if (e.message && (e.message.includes('already') || e.message.includes('disturbed') || e.message.includes('locked'))) {
        console.warn('[Response.json patch] Body already consumed, returning empty object');
        return {};
      }
      throw e;
    }
  };
})();
// ============================================================

import App from "@/App";
import Admin from "@/Admin";
import WorkbookPro from "@/components/WorkbookPro";
import Dashboard from "@/components/Dashboard";
import FamilyConstellation from "@/components/FamilyConstellation";
import PracticePartners from "@/components/PracticePartners";
import PlanetaryMatch from "@/components/PlanetaryMatch";
import PaymentPage from "@/components/PaymentPage";
import Onboarding from "@/components/Onboarding";
import Login from "@/components/Login";
import Directions from "@/components/Directions";
import LorePage from "@/components/LorePage";
import OFNRTrainer from "@/components/OFNRTrainer";
import FeelingsNeedsPage from "@/FeelingsNeedsPage";
import TeachersGuide from "@/components/TeachersGuide";
import NeedsList from "@/components/workbook/NeedsList";
import FeelingsList from "@/components/workbook/FeelingsList";
import BodySensations from "@/components/workbook/BodySensations";
import YourPresence from "@/components/workbook/YourPresence";
import GoldenRule from "@/components/workbook/GoldenRule";
import NVCTreeOfLife from "@/components/workbook/NVCTreeOfLife";
import FeelingsNeedsExercise from "@/components/workbook/FeelingsNeedsExercise";
import DanceFloorsOne from "@/components/workbook/DanceFloorsOne";
import DanceFloorsTwo from "@/components/workbook/DanceFloorsTwo";
import CompassionateDialog from "@/components/workbook/CompassionateDialog";
import FiveKeyPractices from "@/components/workbook/FiveKeyPractices";
import BasicFeelingsNeeds from "@/components/workbook/BasicFeelingsNeeds";
import NVCComponents from "@/components/workbook/NVCComponents";
import EmpathyIsNot from "@/components/workbook/EmpathyIsNot";
import SelfEmpathy from "@/components/workbook/SelfEmpathy";
import Chapter1Intro from "@/components/workbook/Chapter1Intro";
import Chapter1Presentation from "@/components/workbook/Chapter1Presentation";
import Chapter2Presentation from "@/components/workbook/Chapter2Presentation";
import Chapter3Presentation from "@/components/workbook/Chapter3Presentation";
import Chapter4Presentation from "@/components/workbook/Chapter4Presentation";
import Chapter5Presentation from "@/components/workbook/Chapter5Presentation";
import Chapter8Presentation from "@/components/workbook/Chapter8Presentation";
import Page158TranslatingJudgments from "@/components/workbook/Page158TranslatingJudgments";
import CompassionateWheelEnhanced from "@/components/workbook/CompassionateWheelEnhanced";
import CompassionateWheelInteractive from "@/components/workbook/CompassionateWheelInteractive";
import FishbowlInteractive from "@/components/workbook/FishbowlInteractive";
import EmpatheticAlchemy from "@/components/workbook/EmpatheticAlchemy";
import FourBasicChoices from "@/components/workbook/FourBasicChoices";
import FourDsDisconnection from "@/components/workbook/FourDsDisconnection";
import JackalOFNR from "@/components/workbook/JackalOFNR";
import JackalStreetGiraffe from "@/components/workbook/JackalStreetGiraffe";
import Chapter1Homework from "@/components/workbook/Chapter1Homework";
import OFNRStatementsQuestions from "@/components/workbook/OFNRStatementsQuestions";
import OFNRPracticeSheets from "@/components/workbook/OFNRPracticeSheets";
import MutualStrategies from "@/components/workbook/MutualStrategies";
import ConflictShortVersion from "@/components/workbook/ConflictShortVersion";
import ConflictLongVersion from "@/components/workbook/ConflictLongVersion";
import ThreeAreasAttention from "@/components/workbook/ThreeAreasAttention";
import InterpersonalDance from "@/components/workbook/InterpersonalDance";
import InternalMapReality from "@/components/workbook/InternalMapReality";
import CompassionateSuggestions from "@/components/workbook/CompassionateSuggestions";
import CompassionateCommunication from "@/components/workbook/CompassionateCommunication";
import LostLove from "@/components/workbook/LostLove";
import PastInPresent from "@/components/workbook/PastInPresent";
import HomeworkPartnerSchedule from "@/components/workbook/HomeworkPartnerSchedule";
import WellnessInRelationships from "@/components/workbook/WellnessInRelationships";
import PlaceholderPage from "@/components/workbook/PlaceholderPage";
import GratitudeExercises from "@/components/workbook/GratitudeExercises";
import QuadruneBrain from "@/components/workbook/QuadruneBrain";
import GivingReceivingFeedback from "@/components/workbook/GivingReceivingFeedback";
import IMadeItMean from "@/components/workbook/IMadeItMean";
import IntimacyQuestions from "@/components/workbook/IntimacyQuestions";
import CompassionateWheel from "@/components/workbook/CompassionateWheel";
import QuadruneEvolution from "@/components/workbook/QuadruneEvolution";
import NVCKeyIdeas from "@/components/workbook/NVCKeyIdeas";
import NVCPrimer from "@/components/workbook/NVCPrimer";
import ChapterViewer from "@/components/ChapterViewer";
import UserManagementPage from "@/components/UserManagementPage";
import EmergentEmpathy from "@/components/EmergentEmpathy";
import ElchmE from "@/components/ElchmE";
import ElchmESketchboard from "@/components/ElchmESketchboard";
import WisdomLibrary from "@/components/WisdomLibrary";
import ForgotPassword from "@/components/ForgotPassword";
import CompassionateCommunicationChecklist from "@/components/workbook/CompassionateCommunicationChecklist";
import GratitudeCelebrationPractices from "@/components/workbook/GratitudeCelebrationPractices";
import ConnectionRoadmap from "@/components/workbook/ConnectionRoadmap";
import PositiveExperiencesStick from "@/components/workbook/PositiveExperiencesStick";
import FearOfFeelings from "@/components/workbook/FearOfFeelings";
import RiskDreadedExperience from "@/components/workbook/RiskDreadedExperience";
import SayWhatYouMean from "@/components/workbook/SayWhatYouMean";
import { Toaster } from "@/components/ui/sonner";
import GlobalNoteTakingPad from "@/components/GlobalNoteTakingPad";
import MobileEbook from "@/components/MobileEbook";
import ForumPage from "@/components/ForumPage";
import GroupPractice from "@/components/GroupPractice";
import OfferingPage from "@/components/OfferingPage";

// Session validation on app load
const validateSessionOnLoad = async () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Check for return token from ElchmE.com
  const urlParams = new URLSearchParams(window.location.search);
  const returnToken = urlParams.get('session_token') || urlParams.get('sso_token');
  
  if (returnToken) {
    console.log('[Session] Found return token, validating...');
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/validate-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: returnToken })
      });
      
      if (response.ok) {
        // BULLETPROOF FIX: Read body ONLY ONCE as text, then parse
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        console.log('[Session] Token validated, restoring session for:', data.user_name);
        
        // Restore session from token
        localStorage.setItem('workbook_user_id', data.user_id);
        localStorage.setItem('workbook_user_name', data.user_name);
        localStorage.setItem('workbook_user_email', data.user_email);
        
        // Clean up URL (remove token parameter)
        const cleanUrl = window.location.pathname + 
          window.location.search.replace(/[?&](session_token|sso_token)=[^&]*/g, '').replace(/^\?&/, '?').replace(/^\?$/, '');
        window.history.replaceState({}, '', cleanUrl || window.location.pathname);
      }
    } catch (error) {
      console.error('[Session] Token validation failed:', error);
    }
  }
  
  // Verify existing session is still valid
  const userId = localStorage.getItem('workbook_user_id');
  if (userId) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-user/${userId}`);
      if (response.ok) {
        // BULLETPROOF FIX: Read body ONLY ONCE as text, then parse
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        // Update name if changed on server
        if (data.user_name) {
          localStorage.setItem('workbook_user_name', data.user_name);
        }
        console.log('[Session] Session verified for:', data.user_name);
      } else if (response.status === 404) {
        // User no longer exists - clear session
        console.log('[Session] User not found, clearing session');
        localStorage.removeItem('workbook_user_id');
        localStorage.removeItem('workbook_user_name');
        localStorage.removeItem('workbook_user_email');
      }
    } catch (error) {
      // Network error - don't clear session, might be temporary
      console.log('[Session] Could not verify session (offline?)');
    }
  }
};

// Run session validation before rendering
validateSessionOnLoad();

// ScrollToTop component - scrolls to top on every navigation
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ScrollToTop />
      <GlobalNoteTakingPad />
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/login" element={<Login />} />
        <Route path="/workbook" element={<WorkbookPro />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/lore" element={<LorePage />} />
        <Route path="/forum" element={<ForumPage />} />
        <Route path="/group-practice" element={<GroupPractice />} />
        <Route path="/offerings/:offeringId" element={<OfferingPage />} />
        <Route path="/ofnr-trainer" element={<OFNRTrainer />} />
        <Route path="/feelings-needs" element={<FeelingsNeedsPage />} />
        <Route path="/belonging-ebook" element={<TeachersGuide />} />
        <Route path="/belonging-ebook/mobile" element={<MobileEbook />} />
        <Route path="/ebook" element={<MobileEbook />} />
        <Route path="/teachers-guide" element={<Navigate to="/belonging-ebook" replace />} />
        <Route path="/workbook/needs-list" element={<NeedsList />} />
        <Route path="/workbook/feelings-list" element={<FeelingsList />} />
        <Route path="/workbook/body-sensations" element={<BodySensations />} />
        <Route path="/workbook/your-presence" element={<YourPresence />} />
        <Route path="/workbook/golden-rule" element={<GoldenRule />} />
        <Route path="/workbook/nvc-tree-of-life" element={<NVCTreeOfLife />} />
        <Route path="/workbook/dance-floors-one" element={<DanceFloorsOne />} />
        <Route path="/workbook/dance-floors-two" element={<DanceFloorsTwo />} />
        <Route path="/workbook/compassionate-dialog" element={<CompassionateDialog />} />
        <Route path="/workbook/five-key-practices" element={<FiveKeyPractices />} />
        <Route path="/workbook/basic-feelings-needs" element={<BasicFeelingsNeeds />} />
        <Route path="/workbook/feelings-needs-exercise" element={<FeelingsNeedsExercise />} />
        <Route path="/workbook/nvc-components" element={<NVCComponents />} />
        <Route path="/workbook/empathy-is-not" element={<EmpathyIsNot />} />
        <Route path="/workbook/self-empathy" element={<SelfEmpathy />} />
        <Route path="/workbook/chapter-1-intro" element={<Chapter1Intro />} />
        <Route path="/workbook/chapter-1" element={<Chapter1Presentation />} />
        <Route path="/workbook/chapter-2" element={<Chapter2Presentation />} />
        <Route path="/workbook/chapter-3" element={<Chapter3Presentation />} />
        <Route path="/workbook/chapter-4" element={<Chapter4Presentation />} />
        <Route path="/workbook/chapter-5" element={<Chapter5Presentation />} />
        <Route path="/workbook/chapter-8" element={<Chapter8Presentation />} />
        <Route path="/workbook/page-158" element={<Page158TranslatingJudgments />} />
        <Route path="/workbook/four-basic-choices" element={<FourBasicChoices />} />
        <Route path="/workbook/four-ds-disconnection" element={<FourDsDisconnection />} />
        <Route path="/workbook/jackal-ofnr" element={<JackalOFNR />} />
        <Route path="/workbook/jackal-street-giraffe" element={<JackalStreetGiraffe />} />
        <Route path="/workbook/chapter-1-homework" element={<Chapter1Homework />} />
        <Route path="/workbook/ofnr-statements-questions" element={<OFNRStatementsQuestions />} />
        <Route path="/workbook/ofnr-practice-sheets" element={<OFNRPracticeSheets />} />
        <Route path="/workbook/mutual-strategies" element={<MutualStrategies />} />
        <Route path="/workbook/conflict-short" element={<ConflictShortVersion />} />
        <Route path="/workbook/conflict-long" element={<ConflictLongVersion />} />
        <Route path="/workbook/three-areas-attention" element={<ThreeAreasAttention />} />
        <Route path="/workbook/interpersonal-dance" element={<InterpersonalDance />} />
        <Route path="/workbook/internal-map-reality" element={<InternalMapReality />} />
        <Route path="/workbook/compassionate-suggestions" element={<CompassionateSuggestions />} />
        <Route path="/workbook/compassionate-communication" element={<CompassionateCommunication />} />
        <Route path="/workbook/lost-love" element={<LostLove />} />
        <Route path="/workbook/past-in-present" element={<PastInPresent />} />
        <Route path="/workbook/homework-partner-schedule" element={<HomeworkPartnerSchedule />} />
        <Route path="/workbook/wellness-in-relationships" element={<WellnessInRelationships />} />
        <Route path="/workbook/gratitude-exercises" element={<GratitudeExercises />} />
        <Route path="/workbook/quadrune-brain" element={<QuadruneBrain />} />
        <Route path="/workbook/giving-receiving-feedback" element={<GivingReceivingFeedback />} />
        <Route path="/workbook/i-made-it-mean" element={<IMadeItMean />} />
        <Route path="/workbook/intimacy-questions" element={<IntimacyQuestions />} />
        <Route path="/workbook/compassionate-wheel" element={<CompassionateWheel />} />
        <Route path="/workbook/compassionate-wheel-enhanced" element={<CompassionateWheelEnhanced />} />
        <Route path="/workbook/empathetic-alchemy" element={<EmpatheticAlchemy />} />
        <Route path="/workbook/compassionate-wheel-interactive" element={<CompassionateWheelInteractive />} />
        <Route path="/workbook/fishbowl-interactive" element={<FishbowlInteractive />} />
        <Route path="/workbook/page-37" element={<CompassionateCommunicationChecklist />} />
        <Route path="/workbook/page-40" element={<GratitudeCelebrationPractices />} />
        <Route path="/workbook/page-45" element={<ConnectionRoadmap />} />
        <Route path="/workbook/page-55" element={<PositiveExperiencesStick />} />
        <Route path="/workbook/page-57" element={<FearOfFeelings />} />
        <Route path="/workbook/page-59" element={<RiskDreadedExperience />} />
        <Route path="/workbook/quadrune-evolution" element={<QuadruneEvolution />} />
        <Route path="/workbook/nvc-key-ideas" element={<NVCKeyIdeas />} />
        <Route path="/workbook/nvc-primer" element={<NVCPrimer />} />
        <Route path="/workbook/say-what-you-mean" element={<SayWhatYouMean />} />
        <Route path="/workbook/chapter" element={<ChapterViewer />} />
        <Route path="/admin/user/:userId" element={<UserManagementPage />} />
        <Route path="/emergent-empathy" element={<EmergentEmpathy />} />
        <Route path="/elchme" element={<ElchmE />} />
        <Route path="/ElchmE" element={<ElchmE />} />
        <Route path="/alchemy" element={<ElchmE />} />
        <Route path="/elchme/sketchboard" element={<ElchmESketchboard />} />
        <Route path="/elchme/sketchboard/:boardId" element={<ElchmESketchboard />} />
        <Route path="/constellation" element={<FamilyConstellation />} />
        <Route path="/partners" element={<PracticePartners />} />
        <Route path="/planetary" element={<PlanetaryMatch />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/payment-success" element={<PaymentPage />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/directions" element={<Directions />} />
        <Route path="/wisdom" element={<Navigate to="/lore" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ForgotPassword />} />
      </Routes>
      <Toaster />
    </BrowserRouter>
  </React.StrictMode>,
);
